<template lang="html">
  <div class="graph-chart-wrapper pa-0">
    <v-progress-circular
      v-if="loading"
      :size="50"
      color="primary"
      indeterminate
    />
    <div
      id="graph-chart"
      class="chart-empty"
      v-else-if="!hasRequiredParams">
      Please fill in all required fields.
    </div>
    <div
      id="graph-chart"
      class="chart-empty"
      v-else-if="!(data && data.length)">
      No data was found
    </div>
    <div
      v-else
      style="height: 100%; width: 100%;"
    >
      <chart
        id="graph-chart"
        :options="graphOptions"
        style="width: 100%;height:calc(100% - 40px);"
        @clickPoint="clickPoint"
      />
      <div style="height: 40px; display: flex; flex-direction: row;text-align: center;">
        <v-layout row wrap>
          <v-flex
            v-for="(indicator, $index) in indicators"
            :key="$index"
            xs4
            class="pa-2"
          >
            <strong>{{ appSmall ? indicator.labelSmall : indicator.label }}:</strong> {{ indicator.value }}
          </v-flex>
        </v-layout>
      </div>
    </div>
    <DataDetailsDialog
      v-if="showRecord"
      :recordId="showRecord.uid"
      :machineId="params.machine"
      @close="showRecord = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.graph-chart-wrapper {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<script>
import Chart from '@/components/Chart'
import moment from 'moment-timezone'
import get from 'lodash/get'
import TestTypes from '@/lib/TestTypes'
import axios from 'axios'
import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import pick from 'lodash/pick'
import numeral from 'numeral'
// import omit from 'lodash/omit'
import DataDetailsDialog from '../Data/DataDetailsDialog'

let chartColors = ['#7cb5ec', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1', '#434348']

export default {
  components: {
    Chart,
    DataDetailsDialog
  },
  props: {
    params: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      data: [],
      showRecord: null
    }
  },
  computed: {
    isLast10 () {
      return this.params.dateRange && this.params.dateRange.range === 'last10loads'
    },
    hasRequiredParams () {
      if (this.testType && this.params && this.params.machine && (this.isLast10 || (get(this.params, 'dateRange.start') && get(this.params, 'dateRange.start')))) {
        return true
      }
      return false
    },
    testType () {
      if (!this.params || !this.params.testType) {
        return
      }
      let testType = TestTypes.find(t => t.id === this.params.testType)

      if (testType) {
        testType = {
          ...testType,
          ...(typeof testType.chart === 'function' ? { chart: testType.chart() } : {})
        }
      }
      return testType
    },
    indicators () {
      return (this.testType.indicators || []).map(indicator => {
        return {
          label: indicator.label,
          labelSmall: indicator.labelSmall,
          value: numeral(indicator.value(this.data)).format('0,0')
        }
      })
    },
    graphOptions () {
      let vm = this
      let options = {
        chart: {
          zoomType: 'x'
        },
        title: {
          text: get(this.testType, 'chart.name')
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: get(this.testType, 'chart.xAxis.name')
          },
          labels: {
            format: '{value: %m/%d/%Y}'
          },
          lineWidth: 1
        },
        yAxis: {
          title: {
            text: get(this.testType, 'chart.yAxis.name')
          }
        },
        marker: {},
        tooltip: {
          shared: true,
          xDateFormat: '%m/%d/%Y %H:%M:%S'
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          columnrange: {
            dataLabels: {
              enabled: false
            }
          },
          series: {
            animation: false,
            cursor: 'pointer',
            point: {
              events: {
                click: function (e) {
                  vm.clickPoint(e)
                }
              }
            }
          }
        },
        series: []
      }

      get(this.testType, 'chart.series', []).forEach((series, index) => {
        if (series.type === 'columnrange') {
          options.series.push({
            color: chartColors[index],
            name: series.name || '',
            type: series.type,
            marker: {},
            lineWidth: 2,
            ...series,
            data: (this.data || []).map((record) => {
              return [moment(record[series.xKey]).unix() * 1000, record[series.yKey[0]], record[series.yKey[1]]]
            })
          })
        } else {
          options.series.push({
            color: chartColors[index],
            name: series.name || '',
            type: series.type,
            ...series,
            data: (this.data || []).map((record) => {
              let yTransform = series.yTransform || (v => v)
              return [moment(record[series.xKey]).unix() * 1000, yTransform(record[series.yKey])]
            })
          })
        }
      })
      return options
    }
  },
  methods: {
    clickPoint (e) {
      let dataIndex = get(e, 'point.index')
      let data = this.data[dataIndex]
      if (!get(data, 'uid')) {
        return
      }
      console.log('data uid', data, this.params.machine)
      this.showRecord = data
    },
    async runRequest (params, sort = { sortField: 'date', sortDir: 'ASC' }) {
      const aYearAgo = moment().subtract(1, 'year')
      if (
        (params.fromDate && moment(params.fromDate).isBefore(aYearAgo)) ||
        (params.toDate && moment(params.toDate).isBefore(aYearAgo))
      ) {
        params = {
          ...params,
          archive: 1
        }
      }
      return axios({
        method: 'GET',
        url: `/rest/machine/${params.machine}/data`,
        params: {
          ...pick(params, ['current', 'fromDate', 'toDate', 'machine', 'rowCount', 'current', 'archive']),
          ...sort,
          resultGroups: [upperFirst(camelCase(this.params.testType))]
        }
      })
    },
    loadData: async function () {
      let rowCount = this.isLast10 ? 10 : 10000
      console.log('has required', this.hasRequiredParams)
      if (!this.hasRequiredParams) {
        return
      }

      let startDate = this.params.dateRange.start
      let endDate = this.params.dateRange.end

      this.loading = true
      this.data = []

      let page = 1
      let initialResponse
      try {
        let initialOptions = {
          machine: this.params.machine,
          rowCount,
          current: page++
        }
        if (!this.isLast10) {
          if (startDate) {
            initialOptions.fromDate = moment(startDate).format('YYYY-MM-DD[T]HH:mm:ss')
          }
          if (endDate) {
            initialOptions.toDate = moment(endDate).format('YYYY-MM-DD[T]HH:mm:ss')
          }
        }
        let sort
        if ((!startDate && !endDate) || this.isLast10) {
          sort = {
            sortField: 'date',
            sortDir: 'DESC'
          }
        }
        console.log('initial options', initialOptions)
        initialResponse = await this.runRequest(initialOptions, sort)
      } catch (e) {
        this.loading = false
        throw e
      }

      this.data = get(initialResponse.data, 'rows') || []
      console.log('data', this.data)
      if (!this.data.length) {
        this.loading = false
        console.log('returned')
        return
      }

      if (!this.isLast10 && !startDate && !endDate) {
        startDate = moment(this.data[this.data.length - 1].date).toISOString()
        endDate = moment(this.data[0].date).toISOString()
        this.loading = false
        return
      }

      try {
        let promises = []
        if (!this.isLast10 && get(initialResponse.data, 'total') > rowCount) {
          let pages = Math.ceil(get(initialResponse.data, 'total') / rowCount)
          while (page <= pages) {
            promises.push(this.runRequest({
              ...(this.isLast10 ? {
                rowCount: 10
              } : {
                rowCount: 10000,
                fromDate: startDate ? moment(startDate).format('YYYY-MM-DD[T]HH:mm:ss') : null,
                toDate: endDate ? moment(endDate).format('YYYY-MM-DD[T]HH:mm:ss') : null
              }),
              machine: this.params.machine,
              current: page++
            }))
          }
        }

        let responses = await Promise.all(promises)
        this.data = this.data.concat(responses.map(response => get(response, 'data.rows') || []))
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    }
  },
  async created () {
    await this.loadData()
  },
  watch: {
    params (before, after) {
      // No need to reload when only testType changes
      // if (JSON.stringify(omit(before, ['testType'])) === JSON.stringify(omit(after, ['testType']))) {
      //   return
      // }
      this.loadData()
    }
  }
}
</script>
